import { FilterPillsStyleConfigurations, GlobalThemeConfiguration } from '../config.type';
import { defaultFilterPillsConfig } from '../default-constants/default-filter-pills-constant';
import { BorderRadiusKeys } from '../default-constants/global-styling.enum';
import { MappedStyles } from './global-themeing.utils';

export const GLOBAL_FILTER_PILL_STYLING_CLASS = 'global-filter-pill-styling';

const FILTER_PILL_SELECTOR = `#web-app .finx-tag.${GLOBAL_FILTER_PILL_STYLING_CLASS}`;

const RADIUS_PROPERTIES = [
  'border-top-left-radius',
  'border-top-right-radius',
  'border-bottom-left-radius',
  'border-bottom-right-radius',
] as const;

const BORDER_WIDTH_PROPERTIES = [
  'border-top-width',
  'border-right-width',
  'border-bottom-width',
  'border-left-width',
];

const RADIUS_CLASS_VAR_NAMES: Partial<Record<string, string>> = {
  [BorderRadiusKeys.RADIUS_ONE]: 'rad-1',
  [BorderRadiusKeys.RADIUS_TWO]: 'rad-2',
};

/**
 * A pill corner's radius: the theme's radius class corner (`--radius-rad-N-<corner>`) when a radius
 * class is picked, otherwise the radius entered on the corner itself.
 */
export function filterPillBorderRadius(
  borderConfig: Record<string, string> | undefined,
  property:
    | 'border-top-left-radius'
    | 'border-top-right-radius'
    | 'border-bottom-left-radius'
    | 'border-bottom-right-radius',
): string | undefined {
  const radiusClass = RADIUS_CLASS_VAR_NAMES[borderConfig?.['borderRadiusClass'] ?? ''];
  if (radiusClass) {
    const corner = property.replace(/^border-|-radius$/g, '');
    return `var(--radius-${radiusClass}-${corner}, ${borderConfig?.[property] ?? '0'})`;
  }
  return borderConfig?.[property];
}

export function filterPillsStyleDefaults(): FilterPillsStyleConfigurations {
  const { textStyleClass, borderConfig, css_config, colorConfig, iconConfig } =
    structuredClone(defaultFilterPillsConfig);
  return {
    textStyleClass,
    borderConfig: { ...borderConfig, 'border-style': borderConfig.borderStyle },
    css_config,
    colorConfig,
    iconConfig,
  };
}

function declarations(styles: Record<string, string | number | undefined>, important = false): string {
  return Object.entries(styles)
    .filter(([, value]) => value !== undefined && value !== null && value !== '')
    .map(([property, value]) => `${property}: ${value}${important ? ' !important' : ''};`)
    .join(' ');
}

/**
 * Pills render from `--tag-*` vars. An unlinked pill sets them on `:root`; declaring them on the linked
 * pill itself out-ranks that, so the two layers never fight over specificity. A theme saved before
 * filter pills styling existed still styles linked pills, with the widget defaults.
 */
export function mapFilterPillsStylesToCssString(theme: GlobalThemeConfiguration): MappedStyles {
  const config = theme?.filterPillsConfigurations ?? filterPillsStyleDefaults();

  type CssValues = Record<string, string | number | undefined>;
  const css = (config.css_config ?? {}) as CssValues;
  const border = config.borderConfig ?? {};
  const icon = (config.iconConfig?.css_config ?? {}) as CssValues;
  const unit = (value: string | number | undefined, unitKey: string | number | undefined) =>
    value === undefined ? undefined : `${value}${unitKey ?? 'px'}`;

  const pillVars: Record<string, string | number | undefined> = {
    '--tag-height': unit(css['height'], css['dimensionUnit']),
    '--tag-margin-left': unit(css['marginLeft'], css['marginUnit']),
    '--tag-margin-right': unit(css['marginRight'], css['marginUnit']),
    '--tag-padding-left': unit(css['paddingLeft'], css['paddingUnit']),
    '--tag-padding-right': unit(css['paddingRight'], css['paddingUnit']),
    '--tag-box-shadow': [
      css['shadowOffsetX'] ?? 0,
      css['shadowOffsetY'] ?? 0,
      css['shadowBlur'] ?? 0,
      css['shadowSpread'] ?? 0,
      css['shadowColor'] ?? 'black',
    ].join(' '),
    '--tag-border-style': border['border-style'],
    '--tag-active-bg-color': config.colorConfig?.active?.['background-color'],
    '--tag-active-color': config.colorConfig?.active?.textColor?.color,
    '--tag-active-border-color': config.colorConfig?.active?.['border-color'],
    '--tag-inactive-bg-color': config.colorConfig?.idle?.['background-color'],
    '--tag-inactive-color': config.colorConfig?.idle?.textColor?.color,
    '--tag-inactive-border-color': config.colorConfig?.idle?.['border-color'],
    // read back by the pill, since css can't choose which svg a mat-icon renders
    '--tag-icon': config.iconConfig?.icon !== undefined ? JSON.stringify(config.iconConfig.icon) : undefined,
    'margin-top': unit(css['marginTop'], css['marginUnit']),
    'margin-bottom': unit(css['marginBottom'], css['marginUnit']),
  };
  RADIUS_PROPERTIES.forEach(
    (property) => (pillVars[`--tag-${property}`] = filterPillBorderRadius(border, property)),
  );
  BORDER_WIDTH_PROPERTIES.forEach((property) => (pillVars[`--tag-${property}`] = border[property]));

  const result: MappedStyles = {
    [FILTER_PILL_SELECTOR]: declarations(pillVars),
    [`${FILTER_PILL_SELECTOR} mat-icon`]: declarations({
      width: unit(icon['iconSize'], icon['iconUnit']),
      height: unit(icon['iconSize'], icon['iconUnit']),
      color: icon['iconColor'],
      'margin-left': unit(icon['marginLeft'], icon['marginUnit']),
      'margin-right': unit(icon['marginRight'], icon['marginUnit']),
    }),
  };

  // a class name can't be set from css, so the chosen typography style's declarations are copied in
  const typography = config.textStyleClass ? theme.typography?.[config.textStyleClass] : undefined;
  if (typography) {
    result[`${FILTER_PILL_SELECTOR} p`] = declarations(typography, true);
  }
  return result;
}
